import IconDrag from '@assets/icons/drag.svg?react';
import IconMinus from '@assets/icons/minus.svg?react';
import { useDraggable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, IconButton, Theme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TransformedStructure } from './fieldsModalTypes';

export const FIELDS_MODAL_PALETTE_DND_PREFIX = 'fieldsModalPalette:';

const style = {
 cursor: 'grab',
 display: 'flex',
 alignItems: 'center',
 px: 1,
 py: 1,
 borderRadius: '4px',
};

export const TempField = ({
 item,
}: {
 item: TransformedStructure | undefined;
}) => <Box sx={style}>{item?.Translation}</Box>;

export const PaletteDraggableField = ({
 item,
 highlight,
 depth,
}: {
 item: TransformedStructure;
 highlight: string | React.ReactNode;
 depth: number;
}) => {
 const {
  attributes,
  listeners,
  setNodeRef,
  transform,
  isDragging,
 } = useDraggable({
  id: `${FIELDS_MODAL_PALETTE_DND_PREFIX}${item.Key}`,
  data: { fieldKey: item.Key },
 });

 const dynamicStyle = {
  ...style,
  opacity: isDragging ? 0.5 : 1,
  transform: transform ? CSS.Transform.toString(transform) : undefined,
  display: 'flex',
  alignItems: 'center',
  fontSize: '12px',
  background: (theme: Theme) =>
   item.isSelected ? theme.palette.kemek.lightBlue : theme.palette.kemek.bg2,

  color: (theme: Theme) =>
   item.isSelected ? theme.palette.kemek.blue : theme.palette.kemek.darkestGray,
  border: (theme: Theme) =>
   item.isSelected ? `1px solid ${theme.palette.kemek.blue}` : 'none',
  justifyContent: 'space-between',
  ml: depth > 1 ? depth * 0.8 : 0,
 };

 return (
  <Box
   ref={setNodeRef}
   sx={dynamicStyle}
   {...attributes}
   {...listeners}
   data-no-dnd="true"
  >
   <Box sx={{ wordBreak: 'break-all' }}>{highlight}</Box>
  </Box>
 );
};

export const DraggableField = ({
 item,
 highlight,
 depth,
 removeSelectedField,
}: {
 item: TransformedStructure;
 highlight: string | React.ReactNode;
 depth: number;
 removeSelectedField?: (id: string) => void;
}) => {
 const { t } = useTranslation();
 const [isHovered, setIsHovered] = useState(false);
 const {
  attributes,
  listeners,
  setNodeRef,
  transform,
  transition,
  isDragging,
 } = useSortable({ id: item.Key });

 const dynamicStyle = {
  ...style,
  opacity: isDragging ? 0.5 : 1,
  transform: CSS.Transform.toString(transform),
  transition,
  display: 'flex',
  alignItems: 'center',
  fontSize: '12px',
  background: (theme: Theme) =>
   item.isSelected ? theme.palette.kemek.lightBlue : theme.palette.kemek.bg2,

  color: (theme: Theme) =>
   item.isSelected ? theme.palette.kemek.blue : theme.palette.kemek.darkestGray,
  border: (theme: Theme) =>
   item.isSelected ? `1px solid ${theme.palette.kemek.blue}` : 'none',
  justifyContent: 'space-between',
  ml: depth > 1 ? depth * 0.8 : 0,
 };

 const showIcon = isHovered || isDragging;

 return (
  <Box
   ref={setNodeRef}
   sx={dynamicStyle}
   {...attributes}
   {...listeners}
   data-no-dnd="true"
   onMouseEnter={() => setIsHovered(true)}
   onMouseLeave={() => setIsHovered(false)}
  >
   <Box sx={{ wordBreak: 'break-all' }}>{highlight}</Box>
   {removeSelectedField ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
     {showIcon ? <IconDrag style={{ width: 16, height: 16 }} /> : null}
     <IconButton
      sx={{ width: 24, height: 24, p: '4px' }}
      title={t('fieldsModal.removeField')}
      aria-label={t('fieldsModal.removeField')}
      onClick={() => removeSelectedField(item.Key)}
      onPointerDown={(event) => event.stopPropagation()}
     >
      <IconMinus style={{}} />
     </IconButton>
    </Box>
   ) : null}
  </Box>
 );
};
